import React from 'react'
import styles from './MicrositeBlockEditor.module.css'
import { Plus, Trash2, GripVertical, Type, Image as ImageIcon, MousePointerClick, ChevronUp, ChevronDown } from 'lucide-react'

const BLOCK_TYPES = [
    { type: 'text', label: 'Text', icon: Type },
    { type: 'image', label: 'Image', icon: ImageIcon },
    { type: 'cta', label: 'Button', icon: MousePointerClick }
]

const emptyBlock = (type) => {
    if (type === 'text') return { type, heading: '', body: '' }
    if (type === 'image') return { type, url: '', alt: '' }
    if (type === 'cta') return { type, label: '', url: '' }
    return { type }
}

/**
 * MicrositeBlockEditor
 * Simple block content builder for custom pages — text / image /
 * CTA blocks, reorderable. Stores blocks as the `content.blocks`
 * array that gets JSON-encoded by the createCustomPage / 
 * updateCustomPage APIs.
 *
 * Usage:
 *   <MicrositeBlockEditor
 *     blocks={form.content?.blocks || []}
 *     onChange={(blocks) => updateField('content', { blocks })}
 *   />
 */
const MicrositeBlockEditor = ({ blocks = [], onChange }) => {
    const updateBlock = (idx, field, value) => {
        onChange?.(blocks.map((b, i) => (i === idx ? { ...b, [field]: value } : b)))
    }

    const removeBlock = (idx) => {
        onChange?.(blocks.filter((_, i) => i !== idx))
    }

    const addBlock = (type) => {
        onChange?.([...blocks, emptyBlock(type)])
    }

    const moveBlock = (idx, dir) => {
        const newIdx = idx + dir
        if (newIdx < 0 || newIdx >= blocks.length) return
        const next = [...blocks]
        const [moved] = next.splice(idx, 1)
        next.splice(newIdx, 0, moved)
        onChange?.(next)
    }

    return (
        <div className={styles.wrap}>
            {blocks.length === 0 && (
                <div className={styles.emptyState}>No content blocks yet — add one below.</div>
            )}

            <div className={styles.list}>
                {blocks.map((block, idx) => (
                    <div key={idx} className={styles.block}>
                        <div className={styles.blockHeader}>
                            <GripVertical size={14} className={styles.dragHandle} />
                            <span className={styles.blockType}>{block.type}</span>
                            <div className={styles.blockActions}>
                                <button type="button" className={styles.iconBtn} onClick={() => moveBlock(idx, -1)} disabled={idx === 0}>
                                    <ChevronUp size={13} />
                                </button>
                                <button type="button" className={styles.iconBtn} onClick={() => moveBlock(idx, 1)} disabled={idx === blocks.length - 1}>
                                    <ChevronDown size={13} />
                                </button>
                                <button type="button" className={styles.iconBtnDanger} onClick={() => removeBlock(idx)}>
                                    <Trash2 size={13} />
                                </button>
                            </div>
                        </div>

                        <div className={styles.blockBody}>
                            {block.type === 'text' && (
                                <>
                                    <input
                                        type="text"
                                        className={styles.input}
                                        value={block.heading || ''}
                                        onChange={(e) => updateBlock(idx, 'heading', e.target.value)}
                                        placeholder="Heading (optional)"
                                    />
                                    <textarea
                                        className={styles.textarea}
                                        rows={3}
                                        value={block.body || ''}
                                        onChange={(e) => updateBlock(idx, 'body', e.target.value)}
                                        placeholder="Write your content…"
                                    />
                                </>
                            )}

                            {block.type === 'image' && (
                                <>
                                    <input
                                        type="text"
                                        className={styles.input}
                                        value={block.url || ''}
                                        onChange={(e) => updateBlock(idx, 'url', e.target.value)}
                                        placeholder="Image URL"
                                    />
                                    <input
                                        type="text"
                                        className={styles.input}
                                        value={block.alt || ''}
                                        onChange={(e) => updateBlock(idx, 'alt', e.target.value)}
                                        placeholder="Alt text (for accessibility & SEO)"
                                    />
                                </>
                            )}

                            {block.type === 'cta' && (
                                <>
                                    <input
                                        type="text"
                                        className={styles.input}
                                        value={block.label || ''}
                                        onChange={(e) => updateBlock(idx, 'label', e.target.value)}
                                        placeholder="Button label, e.g. Book Now"
                                    />
                                    <input
                                        type="text"
                                        className={styles.input}
                                        value={block.url || ''}
                                        onChange={(e) => updateBlock(idx, 'url', e.target.value)}
                                        placeholder="Button link, e.g. /book"
                                    />
                                </>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            <div className={styles.addRow}>
                {BLOCK_TYPES.map(({ type, label, icon: Icon }) => (
                    <button key={type} type="button" className={styles.addBtn} onClick={() => addBlock(type)}>
                        <Plus size={13} />
                        <Icon size={13} />
                        {label}
                    </button>
                ))}
            </div>
        </div>
    )
}

export default MicrositeBlockEditor